import { useCallback } from "react";
import type { MessageRow, ReceivedEnvelopeRow, ReplyRow, SentEnvelopeRow } from "./api";
import { describe, hidePrivate, mergeCopies, mergeGroups, type CapsuleView } from "./capsules";
import { useSession } from "./session";
import type { ListQuery } from "./messageFilters";
import { useDescribeContext, usePagedViews } from "./usePagedMessages";

/** One reply under a request (PLAN.md 4.21): its row as a view, its number in order of receipt, who paid. */
export type ReplyView = { view: CapsuleView; index: number; stamped: boolean };

/**
 * A request with a reply envelope and its replies, as Correspondence shows it (PLAN.md 4.21): what
 * I sent (`byRecipient`: each recipient's replies) or what I was asked for (`myReplies`).
 */
export type EnvelopeView = {
  id: string;
  gen?: number;
  request: CapsuleView;
  sase: { unlockAt: number | null; expired: boolean };
  /** Sent: what the request's wraps cost me, how many recipients replied, how many there are. */
  paidUnits?: number;
  replied?: number;
  total?: number;
  byRecipient?: { pubkey: string; memberId: number | null; replies: ReplyView[]; more: boolean }[];
  /** Received: my own replies to it. */
  myReplies?: ReplyView[];
  more?: boolean;
};

/** Every capsule view inside an envelope view, the request and the replies alike. */
export function nestedViews(e: EnvelopeView): CapsuleView[] {
  return [e.request, ...(e.byRecipient ?? []).flatMap((p) => p.replies.map((r) => r.view)), ...(e.myReplies ?? []).map((r) => r.view)];
}

function mapViews(e: EnvelopeView, fn: (view: CapsuleView) => CapsuleView): EnvelopeView {
  const reply = (r: ReplyView) => ({ ...r, view: fn(r.view) });
  return {
    ...e,
    request: fn(e.request),
    ...(e.byRecipient ? { byRecipient: e.byRecipient.map((p) => ({ ...p, replies: p.replies.map(reply) })) } : {}),
    ...(e.myReplies ? { myReplies: e.myReplies.map(reply) } : {}),
  };
}

/** Lock: everything that came from the key goes, in the request and every reply. */
const hideEnvelope = (e: EnvelopeView): EnvelopeView => mapViews(e, hidePrivate);

/** Refresh: a fresh view replaces an old one unless the old one holds content opened or revealed under the same Lock count. */
function keepEnvelopes(previous: EnvelopeView[], fresh: EnvelopeView[], locks: number): EnvelopeView[] {
  const old = new Map(previous.flatMap((e) => (e.gen === locks ? nestedViews(e).map((v) => [v.id, v] as const) : [])));
  return fresh.map((e) => mapViews(e, (view) => {
    const was = old.get(view.id);
    return was && ((was.opened !== null && view.opened === null) || (was.fromKey && !view.fromKey)) ? was : view;
  }));
}

/**
 * The two Correspondence lists (PLAN.md 4.21 D11), one page at a time: each request with its copy
 * (sent) and every reply with the replier's copy (received), described like any capsule.
 */
export function useEnvelopes(kind: "sent" | "received", query: ListQuery | null) {
  const { lockGeneration, resolveMembers } = useSession();
  const { withOldKeys } = useDescribeContext(kind === "sent" ? "envelopes_sent" : "envelopes_received");
  // A reply to me is read as an Inbox row (my key opens it); my own reply as a Sent row (with my copy).
  const { withOldKeys: withOldKeysReplies } = useDescribeContext(kind === "sent" ? "envelopes_received" : "envelopes_sent");

  const describePage = useCallback(async (rows: (SentEnvelopeRow | ReceivedEnvelopeRow)[]) => {
    const ctx = await withOldKeys();
    const replyCtx = await withOldKeysReplies();
    const stamp = (view: CapsuleView) => ({ ...view, gen: lockGeneration });
    const one = async (row: MessageRow) => {
      const flat = row.copy ? [row, row.copy] : [row];
      const described = await Promise.all(flat.map((r) => describe(r, ctx)));
      return stamp(mergeGroups(mergeCopies(described))[0]!);
    };
    const reply = async (r: ReplyRow): Promise<ReplyView> => {
      const flat = r.copy ? [r, r.copy] : [r];
      const described = await Promise.all(flat.map((x) => describe(x, replyCtx)));
      return { view: stamp(mergeCopies(described)[0]!), index: r.reply_index, stamped: r.stamped === true };
    };
    const views = await Promise.all(rows.map(async (row): Promise<EnvelopeView> => {
      const request = await one(row);
      const sase = request.sase ?? { unlockAt: null, expired: false };
      if ("by_recipient" in row) {
        const byRecipient = await Promise.all(row.by_recipient.map(async (p) => ({ pubkey: p.pubkey, memberId: p.member_id, replies: await Promise.all(p.replies.map(reply)), more: p.more_replies })));
        return { id: row.id, gen: lockGeneration, request, sase, paidUnits: row.paid_units, replied: row.replied, total: row.total, byRecipient };
      }
      return { id: row.id, gen: lockGeneration, request, sase, myReplies: await Promise.all(row.my_replies.map(reply)), more: row.more_replies };
    }));
    resolveMembers({ keys: views.flatMap(nestedViews).flatMap((view) => [view.author, view.recipient, ...(view.recipients ?? []), ...(view.cc ? [...view.cc.to, ...view.cc.cc].map((n) => n.pubkey) : [])]).filter((key): key is string => !!key) });
    return views;
  }, [withOldKeys, withOldKeysReplies, lockGeneration, resolveMembers]);

  const paged = usePagedViews<SentEnvelopeRow | ReceivedEnvelopeRow, EnvelopeView>({ query, describePage, hide: hideEnvelope, keep: keepEnvelopes });
  /** A capsule inside one of the envelopes was opened or revealed: replace it where it sits. */
  const updateView = (changed: CapsuleView) => paged.setViews((list) => list.map((e) => mapViews({ ...e, gen: lockGeneration }, (view) => (view.id === changed.id ? { ...changed, gen: lockGeneration } : view))));
  const { setViews: _setViews, ...rest } = paged;
  return { ...rest, updateView };
}
